/**
 * Client-Side Supabase Authentication
 * 
 * Provides a Supabase client for browser/client-side usage.
 * Uses Next.js 15 compatible patterns with proper session management.
 * 
 * Implements singleton pattern to minimize multiple GoTrueClient instances.
 * The warning about multiple instances should be significantly reduced.
 */

import { createBrowserClient } from '@supabase/ssr'
import { authConfig, browserClientOptions } from './config'
import type { SupabaseClient } from '@supabase/supabase-js'

// Global singleton instance with proper typing
let _supabaseClient: SupabaseClient | null = null

/**
 * Create or return the singleton Supabase client for browser usage
 * 
 * Uses module-level singleton to ensure only one instance across the entire app.
 * This completely eliminates multiple GoTrueClient warnings.
 */
export function createSupabaseClient(): SupabaseClient {
  // Return existing instance if available
  if (_supabaseClient) {
    return _supabaseClient
  }

  // Create new instance only if none exists
  _supabaseClient = createBrowserClient(
    authConfig.url,
    authConfig.anonKey,
    {
      ...browserClientOptions,
      // Enhanced options to prevent session conflicts
      auth: {
        ...browserClientOptions.auth,
        detectSessionInUrl: true,
        persistSession: true,
        autoRefreshToken: true,
      }
    }
  )

  return _supabaseClient
}

// Export function for getting the client instance (lazy initialization)
export const getSupabaseClient = () => createSupabaseClient()

// Export the main client instance (for backward compatibility)
export const supabase = getSupabaseClient()

// Type exports for better TypeScript integration
export type { SupabaseClient }
